import { useState } from 'react'
import { Button, Dialog, Flex, Select, Text, TextField } from '@radix-ui/themes'
import { PlusIcon } from '@radix-ui/react-icons'
import { FieldShell } from '~/components/FieldShell'
import { useMeterMakerCatalog } from '../useMeterMakers'
import { useMeterModels } from '../useMeterModels'

const NONE = '__none__'

interface MeterModelPickerProps {
  meterMaker: string | null
  value: string | null
  onChange: (model: string | null) => void
}

export const MeterModelPicker = ({ meterMaker, value, onChange }: MeterModelPickerProps) => {
  const { labelOf: makerLabel } = useMeterMakerCatalog()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [newModel, setNewModel] = useState('')
  const modelsQuery = useMeterModels(meterMaker ? [meterMaker] : [])

  const known = modelsQuery.data ?? []
  const models = value && !known.includes(value) ? [value, ...known] : known

  const addModel = () => {
    const model = newModel.trim()
    if (!model) return
    onChange(model)
    setNewModel('')
    setDialogOpen(false)
  }

  return (
    <>
      <FieldShell
        label="Meter model"
        action={
          <Button
            type="button"
            size="1"
            variant="ghost"
            onClick={() => setDialogOpen(true)}
            disabled={!meterMaker}
          >
            <PlusIcon />
            New model
          </Button>
        }
        hint={
          modelsQuery.error ? (
            <Text size="1" color="red">
              Could not load model suggestions.
            </Text>
          ) : null
        }
      >
        <Select.Root
          value={value ?? NONE}
          disabled={!meterMaker || modelsQuery.isLoading}
          onValueChange={(next) => onChange(next === NONE ? null : next)}
        >
          <Select.Trigger
            className="w-full"
            placeholder={meterMaker ? 'Select a model' : 'Select a maker first'}
          />
          <Select.Content>
            <Select.Item value={NONE}>—</Select.Item>
            {models.map((model) => (
              <Select.Item key={model} value={model}>
                {model}
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Root>
      </FieldShell>

      <Dialog.Root open={dialogOpen} onOpenChange={setDialogOpen}>
        <Dialog.Content maxWidth="420px">
          <Dialog.Title>Add meter model</Dialog.Title>
          <Dialog.Description size="2" mb="4">
            Add a model for {meterMaker ? makerLabel(meterMaker) : 'the selected maker'}.
          </Dialog.Description>
          <TextField.Root
            autoFocus
            value={newModel}
            placeholder="e.g. Aquadis+"
            onChange={(event) => setNewModel(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                addModel()
              }
            }}
          />
          <Flex justify="end" gap="2" mt="4">
            <Dialog.Close>
              <Button type="button" variant="soft" color="gray">
                Cancel
              </Button>
            </Dialog.Close>
            <Button type="button" onClick={addModel} disabled={!newModel.trim()}>
              Add model
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </>
  )
}
